import { SignedIn, useUser } from "@clerk/clerk-react";
import { api } from "@convex/_generated/api"; // Use alias
import { useMutation, useQuery } from "convex/react";
import { useEffect, useMemo, useState } from "react";
import QRCodeModal from "@/components/_shared/QRCodeModal";
import Toast from "@/components/_shared/Toast";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { LinkEintrag } from "../link-share/LinkEintrag";
import { LinkPasswordModal } from "../link-share/LinkPasswordModal"; // Import the new modal

// Typ für ein gespeichertes Link-Element
interface LinkItem {
	kuerzel: string; // Use kuerzel as primary ID
	originalUrl: string;
	aufrufe?: number;
	hatPasswort?: boolean; // Add password flag
}

export default function LinkForm() {
	const { isSignedIn } = useUser();
	const [url, setUrl] = useState("");
	const [password, setPassword] = useState(""); // State for password input
	const [isValidUrl, setIsValidUrl] = useState(false);
	const [successMessage, setSuccessMessage] = useState<string | null>(null);
	const [errorMessage, setErrorMessage] = useState<string | null>(null);
	// const [userHash, setUserHash] = useState<string>(""); // Entfernt, da Clerk User ID verwendet wird
	const [lastCreatedLink, setLastCreatedLink] = useState<LinkItem | null>(null);
	const [qrModalOpen, setQrModalOpen] = useState(false);
	const [qrLink, setQrLink] = useState<string>("");
	const [passwordModalOpen, setPasswordModalOpen] = useState(false); // State for password modal
	const [editingLinkKuerzel, setEditingLinkKuerzel] = useState<string | null>(
		null,
	); // State to track which link's password is being edited

	// Convex API Hooks
	const shortenLinkMutation = useMutation(api.linkShares.linkKuerzen);
	// userHash Argument entfernt, da es aus der Authentifizierung kommt
	const userLinksQuery = useQuery(api.linkShares.nutzerLinksLaden, {});

	// Map server data to frontend type, handling undefined query result
	const userLinks: LinkItem[] = useMemo(() => {
		if (!userLinksQuery) return [];
		return userLinksQuery.map((link: {
			kuerzel: string;
			originalUrl: string;
			aufrufe?: number;
			hatPasswort?: boolean;
		}) => ({
			kuerzel: link.kuerzel,
			originalUrl: link.originalUrl,
			aufrufe: link.aufrufe ?? 0,
			hatPasswort: link.hatPasswort ?? false,
		}));
	}, [userLinksQuery]);

	// Keine Ablaufzeit mehr – kein Timer nötig

	// Beim ersten Laden User-Hash erstellen oder laden - ENTFERNT
	// useEffect(() => {
	// const storedHash = localStorage.getItem("linkShares_nutzerHash");
	// if (storedHash) {
	// setUserHash(storedHash);
	// } else {
	// const newHash = uuidv4();
	// localStorage.setItem("linkShares_nutzerHash", newHash);
	// setUserHash(newHash);
	// }
	// }, []);

	// Toast-Messages nach bestimmter Zeit ausblenden
	useEffect(() => {
		if (successMessage) {
			const timer = setTimeout(() => setSuccessMessage(null), 3000);
			return () => clearTimeout(timer);
		}
	}, [successMessage]);

	useEffect(() => {
		if (errorMessage) {
			const timer = setTimeout(() => setErrorMessage(null), 3000);
			return () => clearTimeout(timer);
		}
	}, [errorMessage]);

	// URL-Validierung bei Eingabeänderung
	useEffect(() => {
		try {
			const trimmedUrl = url.trim();
			if (trimmedUrl) {
				let urlToCheck = trimmedUrl;
				if (
					!(
						urlToCheck.startsWith("http://") ||
						urlToCheck.startsWith("https://")
					)
				) {
					urlToCheck = `https://${urlToCheck}`;
				}
				new URL(urlToCheck);
				setIsValidUrl(true);
			} else {
				setIsValidUrl(false);
			}
		} catch (_err) {
			setIsValidUrl(false);
		}
	}, [url]);

	// Link kürzen Funktion
	const shortenLink = async () => {
		// userHash-Prüfung entfernt
		if (!(isValidUrl && url.trim())) return;

		try {
			let finalUrl = url.trim();
			if (
				!(finalUrl.startsWith("http://") || finalUrl.startsWith("https://"))
			) {
				finalUrl = `https://${finalUrl}`;
			}

			// nutzerHash Argument entfernt
			const result = await shortenLinkMutation({
				originalUrl: finalUrl,
				passwort: password.trim() || undefined, // Pass password here
			});

			setLastCreatedLink({
				kuerzel: result.kuerzel,
				originalUrl: finalUrl,
				aufrufe: 0,
				hatPasswort: !!password.trim(), // Set password flag
			});

			setUrl(""); // Clear inputs after success
			setPassword("");
			setSuccessMessage("Link erfolgreich gekürzt!");
		} catch (_err) {
			setErrorMessage(
				"Fehler beim Kürzen des Links. Bitte versuche es erneut.",
			);
		}
	};

	// Link kopieren
	const copyToClipboard = (text: string) => {
		navigator.clipboard.writeText(text).then(
			() => {
				setSuccessMessage("Link in die Zwischenablage kopiert!");
			},
			() => {
				setErrorMessage("Kopieren in die Zwischenablage fehlgeschlagen.");
			},
		);
	};

	// Gibt die Basis-URL des aktuellen Hosts zurück
	const getBaseUrl = () => window.location.origin;

	// Erstelle den vollständigen gekürzten Link
	const getFullShortUrl = (kuerzel: string) =>
		`${getBaseUrl()}/link/${kuerzel}`;

	// QR-Code anzeigen
	const handleShowQR = (kuerzel: string) => {
		setQrLink(getFullShortUrl(kuerzel));
		setQrModalOpen(true);
	};

	// Link-Klick kopiert
	const handleLinkClick = (kuerzel: string) => {
		const fullUrl = getFullShortUrl(kuerzel);
		copyToClipboard(fullUrl);
	};

	// Passwort bearbeiten Modal öffnen
	const handleEditPassword = (kuerzel: string) => {
		setEditingLinkKuerzel(kuerzel);
		setPasswordModalOpen(true);
	};

	// QR-Code-Modal – QR wird direkt im Modal aus Link generiert

	return (
		<div className="flex flex-col gap-8">
			{/* Toasts */}
			<Toast
				message={successMessage || ""}
				onClose={() => setSuccessMessage(null)}
				visible={!!successMessage}
			/>
			<Toast
				message={errorMessage || ""}
				onClose={() => setErrorMessage(null)}
				type="error"
				visible={!!errorMessage}
			/>

			{/* Link-Kürzer Formular */}
			<section className="card rounded-xl border border-gray-100 p-6 shadow-sm backdrop-blur-sm dark:border-gray-700 dark:bg-gray-800/70">
				<h2 className="mb-4 border-gray-100 border-b pb-2 font-semibold text-gray-800 text-xl dark:border-gray-700 dark:text-gray-100">
					Link kürzen
				</h2>
				<div className="space-y-4">
					<div>
						<Label
							className="mb-2 block font-medium text-gray-600 dark:text-gray-300"
							htmlFor="url-input"
						>
							Zu kürzende URL
						</Label>
						<Input
							className="flex-1 border-gray-200 bg-white transition-all focus:ring-2 focus:ring-blue-500 dark:border-gray-700 dark:bg-gray-800 dark:focus:ring-blue-600"
							id="url-input"
							onChange={(e) => setUrl(e.target.value)}
							onKeyDown={(e) => {
								if (e.key === "Enter") {
									e.preventDefault();
									if (isValidUrl) {
										shortenLink();
									}
								}
							}}
							placeholder="z.B. https://example.com/sehr/langer/link/der/gekuerzt/werden/soll"
							value={url}
						/>
					</div>
					<div>
						<Label
							className="mb-2 block font-medium text-gray-600 dark:text-gray-300"
							htmlFor="password-input"
						>
							Passwort (optional)
						</Label>
						<div className="flex gap-2">
							<Input
								className="flex-1 border-gray-200 bg-white transition-all focus:ring-2 focus:ring-blue-500 dark:border-gray-700 dark:bg-gray-800 dark:focus:ring-blue-600"
								id="password-input"
								onChange={(e) => setPassword(e.target.value)}
								placeholder="Leer lassen für keinen Passwortschutz"
								type="password"
								value={password}
							/>
							<Button
								className="transition-all hover:bg-blue-100 dark:hover:bg-blue-900/30"
								disabled={!isValidUrl}
								onClick={shortenLink}
								type="button"
								variant="secondary"
							>
								Link kürzen
							</Button>
						</div>
					</div>
				</div>

				{/* Letzter erstellter Link */}
				{lastCreatedLink && (
					<div className="mt-6 border-gray-100 border-t pt-4 dark:border-gray-700">
						<h3 className="mb-4 font-medium text-gray-700 text-lg dark:text-gray-200">
							Dein gekürzter Link
						</h3>
						<LinkEintrag
							aufrufe={lastCreatedLink.aufrufe ?? 0}
							hatPasswort={lastCreatedLink.hatPasswort}
							istBesitzer={!!isSignedIn}
							kuerzel={lastCreatedLink.kuerzel}
							onEditPassword={handleEditPassword}
							onLinkKlick={handleLinkClick}
							onQrAnzeigen={handleShowQR}
							originalUrl={lastCreatedLink.originalUrl}
						/>
					</div>
				)}
			</section>

			{/* Link-Verlauf */}
			<SignedIn>
				<section className="card rounded-xl border border-gray-100 p-6 shadow-sm backdrop-blur-sm dark:border-gray-700 dark:bg-gray-800/70">

        {userLinks.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-12 text-center">
            <svg
              aria-label="Noch keine Links"
              className="mb-4 h-16 w-16 text-gray-300 dark:text-gray-600"
              fill="none"
              role="img"
              stroke="currentColor"
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={1.5}
              viewBox="0 0 24 24"
              xmlns="http://www.w3.org/2000/svg"
            >
              <title>Noch keine Links</title>
              <path d="M13.828 10.172a4 4 0 00-5.656 0l-4 4a4 4 0 105.656 5.656l1.102-1.101m-.758-4.899a4 4 0 005.656 0l4-4a4 4 0 00-5.656-5.656l-1.1 1.1" />
            </svg>
            <p className="text-gray-500 text-lg dark:text-gray-400">
              Deine gekürzten Links erscheinen hier
            </p>
            <p className="mt-2 text-gray-400 text-sm dark:text-gray-500">
              Kürze einen Link, um ihn in deinem Verlauf zu sehen
            </p>
          </div>
        ) : (
						<div className="space-y-4">
							{userLinks.map((link) => (
								<div
									className="overflow-hidden rounded-lg border border-gray-100 transition-shadow hover:shadow-sm dark:border-gray-700"
									key={link.kuerzel}
								>
									<LinkEintrag
										aufrufe={link.aufrufe ?? 0}
										hatPasswort={link.hatPasswort}
										istBesitzer={true}
										kuerzel={link.kuerzel}
										onEditPassword={handleEditPassword}
										onLinkKlick={handleLinkClick}
										onQrAnzeigen={handleShowQR}
										originalUrl={link.originalUrl}
									/>
								</div>
							))}
						</div>
					)}
				</section>
			</SignedIn>

			{/* QR-Code Modal */}
			<QRCodeModal
				isOpen={qrModalOpen}
				link={qrLink}
				onClose={() => setQrModalOpen(false)}
				title="QR-Code für kürzeren Link"
			/>

			{/* Password Edit Modal */}
			{editingLinkKuerzel && (
				<LinkPasswordModal
					isOpen={passwordModalOpen}
					kuerzel={editingLinkKuerzel}
					onClose={() => {
						setPasswordModalOpen(false);
						setEditingLinkKuerzel(null);
					}}
					onError={(message) => {
						setErrorMessage(message);
						setPasswordModalOpen(false);
						setEditingLinkKuerzel(null);
					}}
					onSuccess={(message) => {
						setSuccessMessage(message);
						setPasswordModalOpen(false);
						setEditingLinkKuerzel(null);
					}}
				/>
			)}
		</div>
	);
}
